<template lang="pug">
PublicEditalLayout
  .editais-page
    section.editais-page__hero
      h1.editais-page__title Seja um credenciado
      p.editais-page__description
        | Participe dos editais de credenciamento do ITERPA para atuar como
        strong  geomensor
        |  ou
        strong  vistoriador
        |  nos processos de regularização fundiária do Pará. Consulte editais abertos, inscreva-se e acompanhe seu processo.

    section.editais-page__features
      FeatureCard(
        v-for="feature in featureCards"
        :key="feature.title"
        :icon="feature.icon"
        :title="feature.title"
        :description="feature.description"
      )

    section.editais-page__editais
      .editais-page__section-header
        h2.editais-page__section-title Editais com inscrições abertas
        hr.editais-page__section-divider
        button.editais-page__history-button(type="button" @click="abrirHistorico")
          span Ver histórico de editais
          i.fas.fa-arrow-right

      .editais-page__cards(v-loading="carregando")
        CardEdital(
          v-for="edital in editais"
          :key="edital.tipoEdital"
          :titulo="edital.titulo"
          :subtitulo="edital.subtitulo"
          :descricao="edital.descricao"
          :publicacao="edital.publicacao"
          :encerramento-inscricoes="edital.encerramentoInscricoes"
          :encerramento-analises="edital.encerramentoAnalises"
          :modalidade="edital.modalidade"
          :inscricoes-abertas="edital.inscricoesAbertas"
          @download="downloadEdital(edital.tipoEdital)"
          @inscricao="navigateInscricao(edital.tipoEdital)"
          @como-funciona="abrirComoFunciona"
        )

        p.editais-page__empty(v-if="!carregando && !editais.length")
          | Nenhum edital disponível no momento.

    section.editais-page__faq(ref="faq")
      h2.editais-page__faq-title Perguntas frequentes sobre credenciamento
      FaqAccordion(:items="faqItems")

    ModalComoFunciona(
      :visible="modalComoFuncionaAberto"
      @close="fecharComoFunciona"
    )

    ModalHistoricoEditais(
      :visible="modalHistoricoAberto"
      @close="fecharHistorico"
    )
</template>

<script>
import PublicEditalLayout from '@/components/editais/PublicEditalLayout'
import FeatureCard from '@/components/editais/FeatureCard'
import CardEdital from '@/components/editais/CardEdital'
import FaqAccordion from '@/components/editais/FaqAccordion'
import ModalComoFunciona from '@/components/editais/ModalComoFunciona'
import ModalHistoricoEditais from '@/components/editais/ModalHistoricoEditais'
import { getEdital } from '@/services/editalService'
import util from '@/utils/utils'
import { FEATURE_CARDS, EDITAL_CONTENT, FAQ_ITEMS } from '@/constants/editaisPublico'

const TIPOS_EDITAL = ['GEOMENSOR', 'VISTORIADOR']

export default {
  name: 'Editais',
  components: {
    PublicEditalLayout,
    FeatureCard,
    CardEdital,
    FaqAccordion,
    ModalComoFunciona,
    ModalHistoricoEditais
  },
  data () {
    return {
      carregando: false,
      modalComoFuncionaAberto: false,
      modalHistoricoAberto: false,
      editaisPorTipo: {
        GEOMENSOR: null,
        VISTORIADOR: null
      },
      featureCards: FEATURE_CARDS,
      faqItems: FAQ_ITEMS
    }
  },
  computed: {
    editais () {
      return TIPOS_EDITAL
        .map((tipo) => this.mapEdital(tipo))
        .filter(Boolean)
    }
  },
  created () {
    this.loadEditais()
  },
  methods: {
    async loadEditais () {
      this.carregando = true

      try {
        const response = await getEdital()
        const editais = Array.isArray(response) ? response : []

        editais.forEach((edital) => {
          if (edital && edital.tipoEdital) {
            this.editaisPorTipo[edital.tipoEdital] = edital
          }
        })
      } finally {
        this.carregando = false
      }
    },

    mapEdital (tipoEdital) {
      const edital = this.editaisPorTipo[tipoEdital]
      const conteudo = EDITAL_CONTENT[tipoEdital]

      if (!edital || !conteudo) {
        return null
      }

      const inscricoesAbertas = Date.now() < edital.dataFim

      return {
        tipoEdital,
        id: edital.id,
        titulo: conteudo.titulo,
        subtitulo: `Nº ${edital.numero} · ITERPA / SICARF · Georreferenciamento Fundiário`,
        descricao: conteudo.descricao,
        publicacao: util.formatDate(edital.dataInicio),
        encerramentoInscricoes: util.formatDate(edital.dataFim),
        encerramentoAnalises: util.formatDate(edital.dataFim),
        modalidade: conteudo.modalidade,
        inscricoesAbertas
      }
    },

    navigateInscricao (tipoEdital) {
      const edital = this.editaisPorTipo[tipoEdital]

      if (!edital || !edital.id) {
        return
      }

      this.$router.push({
        name: 'credenciados/cadastro',
        params: { idEdital: edital.id }
      })
    },

    downloadEdital (tipoEdital) {
      const edital = this.editaisPorTipo[tipoEdital]

      if (!edital || !edital.id) {
        return
      }

      const urlBase = process.env.VUE_APP_ROOT_URL
      window.open(`${urlBase}/publico/edital/baixar?idEdital=${edital.id}`, '_blank')
    },

    abrirComoFunciona () {
      this.modalComoFuncionaAberto = true
    },

    fecharComoFunciona () {
      this.modalComoFuncionaAberto = false
    },

    abrirHistorico () {
      this.modalHistoricoAberto = true
    },

    fecharHistorico () {
      this.modalHistoricoAberto = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Manrope:wght@700&family=Poppins:wght@600&family=Plus+Jakarta+Sans:wght@700&display=swap');

.editais-page {
  width: 100%;
}

.editais-page__title {
  color: #1e174e;
  font-family: 'Poppins', 'Ubuntu', sans-serif;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.15;
  margin: 0 0 4px;
}

.editais-page__description {
  color: #4c4c4c;
  font-family: 'Inter', 'Ubuntu', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 24px;
  max-width: 1206px;

  strong {
    font-weight: 500;
  }
}

.editais-page__features {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
}

.editais-page__editais {
  margin-bottom: 48px;
}

.editais-page__section-header {
  align-items: center;
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.editais-page__section-title {
  color: #8a8a8a;
  font-family: 'Inter', 'Ubuntu', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.08em;
  line-height: 1.43;
  margin: 0;
  text-transform: uppercase;
  white-space: nowrap;
}

.editais-page__section-divider {
  background: #eaeaea;
  border: 0;
  flex: 1;
  height: 1px;
  margin: 0;
}

.editais-page__history-button {
  align-items: center;
  background: transparent;
  border: none;
  color: #141414;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Inter', 'Ubuntu', sans-serif;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  white-space: nowrap;

  i {
    font-size: 12px;
  }
}

.editais-page__cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.editais-page__empty {
  color: #8a8a8a;
  font-family: 'Inter', 'Ubuntu', sans-serif;
  font-size: 14px;
  margin: 0;
  text-align: center;
}

.editais-page__faq-title {
  color: #1e174e;
  font-family: 'Poppins', 'Ubuntu', sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.15;
  margin: 0 0 24px;
}

@media (max-width: 1024px) {
  .editais-page__features {
    flex-direction: column;
  }
}

@media (max-width: 640px) {
  .editais-page__section-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .editais-page__section-divider {
    width: 100%;
  }
}
</style>
